import React, { lazy, useState, useEffect } from 'react';
import { 
  ThemeProvider, 
  createTheme, 
  CssBaseline, 
  AppBar, 
  Toolbar, 
  Typography, 
  Container, 
  Box,
  Button,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LandingPage from './components/LandingPage';
import AsyncView from './components/common/AsyncView';
import { useAuth } from './contexts/AuthContext';
import { trackPageView } from './config/appInsights';
import './App.css';

const ProjectSelector = lazy(() => import('./components/ProjectSelector'));
const ProjectDashboard = lazy(() => import('./components/ProjectDashboard'));
const PropertyInspectionDashboard = lazy(() => import('./components/PropertyInspectionDashboard'));
const DemoPage = lazy(() => import('./components/DemoPage'));
const SemanticKernelChat = lazy(() => import('./components/SemanticKernelChat'));
const AssetPortfolio = lazy(() => import('./components/AssetPortfolio'));
const PresentationDashboard = lazy(() => import('./components/presentation/PresentationDashboard'));
const SharedReportViewer = lazy(() => import('./components/report/SharedReportViewer'));

const theme = createTheme({
  palette: {
    primary: {
      main: '#0EA5E9', // KlaraView cyan blue
      light: '#38BDF8',
      dark: '#0284C7',
    },
    secondary: {
      main: '#22C55E', // KlaraView green
      light: '#4ADE80',
      dark: '#16A34A',
    },
    background: {
      default: '#F8FAFC', // Clean slate background
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0F172A', // Deep slate text
      secondary: '#475569',
    },
  },
  typography: {
    fontFamily: '"Inter", "IBM Plex Sans", "Segoe UI", "Roboto", sans-serif',
    h1: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontWeight: 500,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontWeight: 500,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 500,
    },
    body1: {
      lineHeight: 1.7,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#0F172A',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

interface Project {
  project_id: string;
  project_name: string;
  project_type: string;
  description?: string;
  customer_id: string;
  customer_name: string;
  customer_contact?: string;
  customer_email?: string;
  status: string;
  start_date: string;
  expected_completion_date?: string;
  total_inspections: number;
  completed_inspections: number;
  pending_inspections: number;
  allowed_inspection_types: string[];
  auto_analysis_enabled: boolean;
  created_timestamp: string;
  updated_timestamp: string;
}

interface Asset {
  id: string;
  customer_id: string;
  name: string;
  address: string;
  main_image_url?: string;
  main_image_upload_date?: string;
  main_image_source?: 'uploaded' | 'inspection' | 'placeholder';
  thumbnail_url?: string;
  thumbnail_blob_name?: string;
  facade_area_sqm: number;
  facade_type: 'brick' | 'concrete' | 'glass' | 'stone' | 'mixed';
  construction_year: number;
  last_renovation_year?: number;
  co2_savings_last_renovation?: number;
  coordinates: {
    lat: number;
    lng: number;
  };
  active_projects: number;
  total_inspections: number;
  risk_level: 'low' | 'medium' | 'high';
  description?: string;
  // Real defect type counts from backend
  total_issues?: number;
  high_severity_issues?: number;
  medium_severity_issues?: number;
  low_severity_issues?: number;
  crack_count?: number;
  flaking_count?: number;
  spalling_count?: number;
  rust_count?: number;
  anomaly_count?: number;
  setting_count?: number;
  // Severity-specific defect type counts
  high_severity_crack_count?: number;
  high_severity_spalling_count?: number;
  high_severity_flaking_count?: number;
  high_severity_rust_count?: number;
}

type AppView = 'landing' | 'portfolio' | 'project' | 'demo' | 'presentation' | 'shared';

function App() {
  const { user, isLoading, isAuthenticated, login, logout, loginWithDemoAccount } = useAuth();
  const [currentView, setCurrentView] = useState<AppView>('landing');
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  // Auto-login via URL parameter (e.g., ?demo=vasakronan)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const demoParam = urlParams.get('demo');
    
    if (demoParam && !isAuthenticated && !isLoading) {
      const email = `${demoParam}@demo.com`;
      console.log('Attempting auto-login with demo account:', email);
      
      const success = loginWithDemoAccount(email);
      if (success) {
        console.log('Auto-login successful, cleaning URL');
        // Clean URL to remove the parameter
        window.history.replaceState({}, '', window.location.pathname);
        setCurrentView('portfolio');
      } else {
        console.error('Auto-login failed for:', email);
      }
    }
  }, [isAuthenticated, isLoading, loginWithDemoAccount]);

  // Detect shared report URL (on mount and on navigation)
  useEffect(() => {
    const checkSharedPath = () => {
      if (window.location.pathname.startsWith('/shared/')) {
        setCurrentView('shared');
      }
    };
    checkSharedPath();
    window.addEventListener('popstate', checkSharedPath);
    return () => window.removeEventListener('popstate', checkSharedPath);
  }, []);

  // Usage telemetry: record each state-driven view change as a page view.
  // The app has no router, so navigation is the AppView state transition.
  useEffect(() => {
    trackPageView(`view:${currentView}`);
  }, [currentView]);

  // Shared report view — no auth required, renders its own shell
  if (currentView === 'shared') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AsyncView name="Rapporten"><SharedReportViewer /></AsyncView>
      </ThemeProvider>
    );
  }

  // Show loading screen while auth status is being determined
  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading KlaraView Portal...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  // If not authenticated, show the public marketing site full-bleed.
  // MarketingShell brings its own nav, footer, and cream background — we
  // intentionally do NOT wrap it in the app's AppBar / Container, otherwise
  // the editorial layout becomes a floating card on a grey page.
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LandingPage onLogin={login} />
      </ThemeProvider>
    );
  }

  // Set default view to portfolio if authenticated and still on landing
  if (isAuthenticated && currentView === 'landing') {
    setCurrentView('portfolio');
  }

  const handleBackToLanding = () => {
    logout();
  };

  const handleAssetSelect = (asset: Asset) => {
    setSelectedAsset(asset);
    // Load inspections for this property directly
    setCurrentView('project');
  };

  const handleBackToPortfolio = () => {
    setSelectedAsset(null);
    setSelectedProject(null);
    setCurrentView('portfolio');
  };

  const handleProjectSelect = (project: Project | null) => {
    setSelectedProject(project);
  };

  const handleShowDemo = () => {
    setCurrentView('demo');
  };

  const handleShowPresentation = () => {
    setCurrentView('presentation');
  };

  const handleProjectChange = () => {
    // This will trigger a refresh of project data
    // The individual components will handle their own refresh mechanisms
  };

  const getAppBarTitle = () => {
    switch (currentView) {
      case 'portfolio':
        return `KlaraView Portal - ${user?.customerName || 'My Properties'}`;
      case 'project':
        return selectedAsset 
          ? `KlaraView Portal - ${selectedAsset.name}`
          : selectedProject
          ? `KlaraView Portal - ${selectedProject.project_name}`
          : 'KlaraView Portal - Property Inspections';
      case 'demo':
        return 'KlaraView Portal - Advanced AI Demo';
      case 'presentation':
        return 'KlaraView Portal - Executive Presentation Dashboard';
      default:
        return 'KlaraView Portal';
    }
  };

  const showBackButton = currentView !== 'portfolio';
  const handleBackClick = () => {
    switch (currentView) {
      case 'project':
        if (selectedAsset) {
          handleBackToPortfolio();
        } else {
          handleBackToPortfolio();
        }
        break;
      case 'demo':
        handleBackToPortfolio();
        break;
      case 'presentation':
        handleBackToPortfolio();
        break;
      default:
        handleBackToPortfolio();
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            {showBackButton && (
              <Button
                color="inherit"
                onClick={handleBackClick}
                startIcon={<ArrowBackIcon />}
                sx={{ mr: 2 }}
              >
                Back
              </Button>
            )}
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {getAppBarTitle()}
            </Typography>
            
            {/* Demo Button - Only show for demo user (local development) */}
            {currentView !== 'demo' && currentView !== 'presentation' && 
             user?.userId === 'demo-user-123' && (
              <Button
                color="inherit"
                onClick={handleShowDemo}
                sx={{
                  backgroundColor: 'rgba(136, 192, 208, 0.1)',
                  color: '#88C0D0',
                  fontWeight: 600,
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  mr: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(136, 192, 208, 0.2)',
                  }
                }}
              >
                🔬 AI Demo
              </Button>
            )}

            {/* Presentation Button - Only show for demo user (local development) */}
            {currentView !== 'presentation' && currentView !== 'demo' && 
             user?.userId === 'demo-user-123' && (
              <Button
                color="inherit"
                onClick={handleShowPresentation}
                sx={{
                  backgroundColor: 'rgba(163, 190, 140, 0.1)',
                  color: '#A3BE8C',
                  fontWeight: 600,
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  mr: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(163, 190, 140, 0.2)',
                  }
                }}
              >
                📊 Presentation
              </Button>
            )}

            {/* User info and logout */}
            <Button
              color="inherit"
              onClick={logout}
              sx={{ ml: 1 }}
            >
              {user?.name} - Sign Out
            </Button>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
          <AsyncView key={currentView} name="Vyn">
          {currentView === 'portfolio' && (
            <AssetPortfolio 
              user={user}
              onAssetSelect={handleAssetSelect}
              onBackToLanding={handleBackToLanding}
            />
          )}
          
          {currentView === 'project' && (
            <Box>
              {selectedAsset ? (
                <Box>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" gutterBottom sx={{ color: '#2F3E46', fontWeight: 400 }}>
                      {selectedAsset.name}
                    </Typography>
                    <Typography variant="h6" gutterBottom sx={{ color: '#4C566A', mb: 3 }}>
                      Property Inspection Dashboard
                    </Typography>
                  </Box>
                  
                  {/* Property Inspection Management - Skip project selector, go straight to inspections */}
                  <PropertyInspectionDashboard 
                    property={selectedAsset}
                    user={user}
                    onPropertyChange={() => {/* Refresh property data */}}
                    onBackToPortfolio={handleBackToPortfolio}
                  />
                </Box>
              ) : selectedProject ? (
                <ProjectDashboard 
                  project={selectedProject} 
                  user={user}
                  onProjectChange={handleProjectChange}
                />
              ) : (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ color: '#4C566A', mb: 3 }}>
                    Select or create an inspection project for this property
                  </Typography>
                  <ProjectSelector 
                    selectedProject={selectedProject}
                    user={user}
                    onProjectSelect={handleProjectSelect} 
                  />
                </Box>
              )}
            </Box>
          )}
          
          {currentView === 'demo' && (
            <DemoPage />
          )}

          {currentView === 'presentation' && (
            <PresentationDashboard />
          )}
          </AsyncView>
        </Container>
      </Box>

      {/* AI Chat Assistant - Always Available */}
      <AsyncView name="Chatten" compact><SemanticKernelChat /></AsyncView>
    </ThemeProvider>
  );
}

export default App;
